package tech.gensert.portal.web.showcase.utils

import kotlinx.browser.localStorage
import kotlinx.browser.window
import react.router.useNavigate
import react.useEffect
import react.useEffectOnce
import react.useRef
import react.useState
import tech.gensert.portal.common.model.config.CookieNames
import tech.gensert.portal.common.model.config.getCookie
import tech.gensert.portal.web.config.cookies.clearCookies
import tech.gensert.portal.web.config.cookies.storeUserAccount
import tech.gensert.portal.web.showcase.reusables.jsObject
import tech.gensert.portal.web.wrappers.useMsal

fun useTokenManager(): Pair<TokenManager, Boolean> {
    val context = useMsal()
    val instance = context.instance
    val navigate = useNavigate()
    val hint = getCookie<String>(CookieNames.gt_login_hint)?.replace("\"", "")
    val (msalReady, setMsalReady) = useState(false)
    useEffectOnce {
        instance.initialize()
            .then {
                val authData = instance.getActiveAccount()
                if (authData != null) {
                    instance.setActiveAccount(authData)
                }
                setMsalReady(true)
            }
            .catch { error ->
                console.error("❌ MSAL initialization failed:", error)
            }
    }

    useEffect(listOf(msalReady)) {
        if (msalReady) {
            instance.handleRedirectPromise()
                .then { authResult ->
                    if (authResult?.account != null) {
                        storeUserAccount(authResult)
                        instance.setActiveAccount(authResult.account)
                        if (window.location.pathname == "/") {
                            navigate("/services")
                        }
                    } else if (instance.getActiveAccount() == null) {
                        navigate("/")
                    }
                }
                .catch { error ->
                    console.error("❌ Error during redirect handling:", error)
                    navigate("/")
                }
        }
    }
    val isMsalInitialized = msalReady
    val tokenRequest = jsObject<dynamic> {
        this.loginHint = hint
    }

    fun login(onSuccess: (String) -> Unit = {}, onError: (Throwable) -> Unit = {}) {
        if (!isMsalInitialized) {
            return
        }
        val activeAccount = instance.getActiveAccount()
        val loginRequest = jsObject<dynamic> {
            if (activeAccount != null) {
                this.account = activeAccount
            }
        }
        instance.loginRedirect(loginRequest)
            .catch { error ->
                onError(error)
            }
    }

    fun acquireTokenSilently(onSuccess: (String) -> Unit, onError: (Throwable) -> Unit) {
        if (!isMsalInitialized) {
            return
        }
        instance.ssoSilent(tokenRequest)
            .then { authResult ->
                storeUserAccount(authResult)
                instance.setActiveAccount(authResult.account)
                onSuccess(authResult.accessToken)
                if (window.location.pathname == "/") {
                    navigate("/services")
                }
            }
            .catch { error ->
                login(onSuccess, onError)
            }
    }

    fun getToken(onSuccess: (String) -> Unit, onError: (Throwable) -> Unit) {
        if (!isMsalInitialized) {
            return
        }
        acquireTokenSilently(
            onSuccess = {
                onSuccess(it)
                if (window.location.pathname == "/") {
                    navigate("/services")
                }
            },
            onError = {
                login(onSuccess, onError)
            }
        )
    }

    fun isAuthenticated(): Boolean {
        return instance.getActiveAccount() != null
    }

    val intervalIdRef = useRef<dynamic>(null)

    useEffectOnce {
        if (hint != null && !isAuthenticated()) {
            acquireTokenSilently({}, {
                navigate("/")
            })
        }

        intervalIdRef.current = window.setInterval({
            val isLocalCleared = localStorage.length == 0

            var hasMsalKey = false
            for (i in 0 until localStorage.length) {
                val key = localStorage.key(i) ?: continue
                if (key.startsWith("msal.token.keys") && localStorage.getItem(key) != null) {
                    hasMsalKey = true
                    break
                }
            }
            if (isLocalCleared || !hasMsalKey) {
                clearCookies()
                window.clearInterval(intervalIdRef.current as Int)
                window.location.href = "/"
            }
        }, 1000)

        cleanup {
            window.clearInterval(intervalIdRef.current as Int)
        }
    }
    return Pair(
        TokenManager(
            getToken = ::getToken,
            login = ::login,
            isAuthenticated = ::isAuthenticated
        ),
        msalReady
    )
}

data class TokenManager(
    val getToken: (onSuccess: (String) -> Unit, onError: (Throwable) -> Unit) -> Unit,
    val login: (onSuccess: (String) -> Unit, onError: (Throwable) -> Unit) -> Unit,
    val isAuthenticated: () -> Boolean
)